import { useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useNotifications } from '@/components/ui/NotificationSystem';
import { navigateApp } from '@/lib/navigation';

export interface NewSmsEvent {
    conversation_id: string;
    company_id: string;
    phone_number_id: string;
    peer_e164: string;
    message_preview: string;
}

export interface SmsStatusUpdatedEvent {
    message_id: string;
    conversation_id: string;
    company_id: string;
    status: string;
    /** Provider error code for a failed/undelivered message (e.g. Telnyx 40010). Absent on success. */
    error_code?: number | null;
    /** Human-readable provider error detail for a failed/undelivered message. Absent on success. */
    error_message?: string | null;
}

type NewSmsHandler = (event: NewSmsEvent) => void;
type SmsStatusUpdatedHandler = (event: SmsStatusUpdatedEvent) => void;

export const useVoipSmsNotifications = (connection: signalR.HubConnection | null) => {
    const { addNotification } = useNotifications();
    const subscribersRef = useRef<Set<NewSmsHandler>>(new Set());
    const backgroundSubscribersRef = useRef<Set<NewSmsHandler>>(new Set());
    const statusSubscribersRef = useRef<Set<SmsStatusUpdatedHandler>>(new Set());
    const statusTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const bufferedStatusEventsRef = useRef<Map<string, SmsStatusUpdatedEvent>>(new Map());
    const bufferedStatusCleanupRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const subscribeNewSms = useCallback((handler: NewSmsHandler) => {
        subscribersRef.current.add(handler);
        return () => {
            subscribersRef.current.delete(handler);
        };
    }, []);

    const subscribeNewSmsBackground = useCallback((handler: NewSmsHandler) => {
        backgroundSubscribersRef.current.add(handler);
        return () => {
            backgroundSubscribersRef.current.delete(handler);
        };
    }, []);

    const subscribeSmsStatusUpdated = useCallback((handler: SmsStatusUpdatedHandler) => {
        statusSubscribersRef.current.add(handler);
        return () => {
            statusSubscribersRef.current.delete(handler);
        };
    }, []);

    const consumeLatestSmsStatus = useCallback((messageId: string) => {
        const event = bufferedStatusEventsRef.current.get(messageId);
        if (!event) {
            return undefined;
        }

        bufferedStatusEventsRef.current.delete(messageId);

        const cleanup = bufferedStatusCleanupRef.current.get(messageId);
        if (cleanup) {
            clearTimeout(cleanup);
            bufferedStatusCleanupRef.current.delete(messageId);
        }

        return event;
    }, []);

    useEffect(() => {
        if (!connection) return;

        const handleNewSms = (event: NewSmsEvent) => {
            const hasForegroundSubscriber = subscribersRef.current.size > 0;

            subscribersRef.current.forEach((handler) => handler(event));
            backgroundSubscribersRef.current.forEach((handler) => handler(event));

            if (!hasForegroundSubscriber) {
                addNotification({
                    type: 'info',
                    title: `New SMS from ${event.peer_e164}`,
                    message: event.message_preview,
                    duration: 5000,
                    onClick: () => {
                        navigateApp(`/messaging?conversation_id=${encodeURIComponent(event.conversation_id)}`);
                    },
                });
            }
        };

        const handleSmsStatusUpdated = (event: SmsStatusUpdatedEvent) => {
            bufferedStatusEventsRef.current.set(event.message_id, event);

            const existingCleanup = bufferedStatusCleanupRef.current.get(event.message_id);
            if (existingCleanup) {
                clearTimeout(existingCleanup);
            }

            const cleanup = setTimeout(() => {
                bufferedStatusEventsRef.current.delete(event.message_id);
                bufferedStatusCleanupRef.current.delete(event.message_id);
            }, 30000);

            bufferedStatusCleanupRef.current.set(event.message_id, cleanup);

            const existingTimer = statusTimersRef.current.get(event.message_id);
            if (existingTimer) {
                clearTimeout(existingTimer);
            }

            const timer = setTimeout(() => {
                statusTimersRef.current.delete(event.message_id);
                statusSubscribersRef.current.forEach((handler) => handler(event));
            }, 500);

            statusTimersRef.current.set(event.message_id, timer);
        };

        connection.on('NewSms', handleNewSms);
        connection.on('SmsStatusUpdated', handleSmsStatusUpdated);

        return () => {
            connection.off('NewSms', handleNewSms);
            connection.off('SmsStatusUpdated', handleSmsStatusUpdated);
            statusTimersRef.current.forEach((timer) => clearTimeout(timer));
            statusTimersRef.current.clear();
            bufferedStatusCleanupRef.current.forEach((timer) => clearTimeout(timer));
            bufferedStatusCleanupRef.current.clear();
            bufferedStatusEventsRef.current.clear();
        };
    }, [connection, addNotification]);

    return {
        subscribeNewSms,
        subscribeNewSmsBackground,
        subscribeSmsStatusUpdated,
        consumeLatestSmsStatus,
    };
};